import React, { Component } from "react";
import PropTypes from "prop-types";
import { translate } from "react-i18next";
import moment from "moment-timezone";
import debug from "debug";
import TranslatedHeader from "./components/Header";
import DefaultFlow from "./flows/default";
import { ENDPOINT, ERROR_CODES } from "./utils/config";
import { processUTMParams } from "./utils/utm";
import { postSignup } from "./utils/hubspot";

const log = debug("velpic:signup:redirect");
let gaTimeout = null;

function redirectTo(url) {
	clearTimeout(gaTimeout);
	try {
		document.location.replace(url);
	} catch (err) {
		// istanbul ignore next
		log(
			"Failed to redirect using location.replace, falling back to location.href:",
			err
		);
		// istanbul ignore next
		document.location.href = url;
	}
}

const getLoginUrl = () => {
	// Extract base URL without query parameters and hash
	const url = new URL(window.location.href);
	const baseUrl = `${url.protocol}//${url.host}${url.pathname}`;

	if (baseUrl === "https://signup.au.damstralearning.com/") {
		return "https://app.au.damstralearning.com/";
	}
	if (baseUrl === "https://signup.eu.learning.ideagen.com/") {
		return "https://app.eu.learning.ideagen.com/";
	}
	if (baseUrl === "https://signup.us.damstralearning.com/") {
		return "https://app.us.damstralearning.com/";
	}
	if (baseUrl === "https://qa-signup.velpic.com/") {
		return "https://qa-app.velpic.com/";
	}
	if (baseUrl === "https://signup.dev.damstralearning.com/") {
		return "https://app.dev.damstralearning.com/";
	}

	return "https://app.velpic.com/";
};

function handleSuccess(response) {
	const ga = window.ga;

	if (response.clientId && response.userId && typeof ga !== "undefined") {
		// If GA doesn't hit the callback after 5 seconds auto-redirect
		gaTimeout = setTimeout(() => {
			redirectTo(response.autoLoginUrl);
		}, 5000);

		ga("create", "UA-47154704-3", { name: "velpictrial" });
		ga("velpictrial.send", "pageview", "/free-trial/complete.html");
		ga("velpictrial.send", "event", {
			eventCategory: "Trial Signup",
			eventAction: response.clientId,
			eventLabel: response.userId,
			hitCallback: redirectTo.bind(null, response.autoLoginUrl),
		});
	} else {
		redirectTo(response.autoLoginUrl);
	}
}

class App extends Component {
	constructor(props) {
		super(props);

		this.state = {
			processing: false,
			errMsg: false,
			utm: null,
		};

		this.flowCompleteHandler = this.flowCompleteHandler.bind(this);
		this.handleError = this.handleError.bind(this);
		this.resetErrorMessage = this.resetErrorMessage.bind(this);
	}

	componentDidMount() {
		const utm = processUTMParams();

		this.setState({
			utm,
		});
	}

	resetErrorMessage() {
		this.setState({
			errMsg: false,
		});
	}

	handleError(err) {
		const { t, i18n } = this.props;
		if (!err) {
			return;
		}

		let message = err.message || err;

		if (err.internalCode) {
			message = err.message;

			if (err.internalCode === ERROR_CODES.MISSING_PARAMETER) {
				message = err.message.split(",");

				const errFields = {};

				message.forEach(field => {
					const trimmedField = field.trim();
					if (trimmedField === "firstname" || trimmedField === "lastname") {
						errFields[t("labels.firstLast")] = true;
					} else {
						errFields[
							i18n.exists(`labels.${field}`) ? t(`labels.${field}`) : field
						] = true;
					}
				});

				message = Object.keys(errFields).join(", ");
			}

			message = this.props.i18n.exists(`error.internalCode.${err.internalCode}`)
				? t(`error.internalCode.${err.internalCode}`, { message })
				: err.message;
		} else if (err.externalCode) {
			/* istanbul ignore next */
			message = this.props.i18n.exists(`error.externalCode.${err.internalCode}`)
				? t(`error.externalCode.${err.internalCode}`, { message })
				: err.message;
		}

		this.setState({
			errMsg: message,
		});
	}

	flowCompleteHandler(inputs) {
		const body = {
			...inputs,
			tmz: moment.tz.guess(),
			...this.state.utm,
		};

		if (body.name) {
			const fullName = body.name.split(" ");

			if (fullName.length === 1) {
				body.firstname = fullName[0];
				body.lastname = "";
			} else {
				body.firstname = fullName.shift();
				body.lastname = fullName.join(" ");
			}

			delete body.name;
		}

		postSignup(body);

		const myHeaders = new Headers();
		myHeaders.append("Content-Type", "application/json");

		this.setState({
			processing: true,
			errMsg: false,
		});

		fetch(ENDPOINT, {
			method: "POST",
			mode: "cors",
			headers: myHeaders,
			body: JSON.stringify(body),
		})
			.then(
				resp => {
					return resp.json();
				},
				err => {
					this.handleError(err);
					return null;
				}
			)
			.then(response => {
				if (!response) {
					this.setState({
						processing: false,
					});
					return;
				}

				if (response.error) {
					this.setState({
						processing: false,
					});
					this.handleError(response);
					return;
				}

				handleSuccess(response);
			});
	}

	render() {
		return (
			<main role="main">
				<TranslatedHeader loginUrl={getLoginUrl()} />
				<DefaultFlow
					onComplete={this.flowCompleteHandler}
					{...this.state}
					resetErrorMessage={this.resetErrorMessage}
				/>
			</main>
		);
	}
}

App.propTypes = {
	t: PropTypes.func,
	i18n: PropTypes.object,
};

export { App };

export default translate()(App);
